// ============================================================================
// Lectura de CSV para importar contactos (C4).
//
// Sin librería, a propósito: el formato que hay que aguantar es el que sale de
// Excel y de Google Sheets en un Mac colombiano, y eso son cuatro casos
// concretos, no el universo entero de RFC 4180.
//
// Lo que SÍ hay que aguantar, porque pasa siempre:
//  · separador `;` — Excel en español lo usa por defecto, no la coma;
//  · comillas alrededor de campos con coma dentro ("Pérez, Ana");
//  · comillas dobladas dentro del campo ("dijo ""hola""");
//  · BOM al inicio (Excel lo mete y convierte la primera cabecera en basura);
//  · saltos de línea CRLF;
//  · cabeceras con tildes, mayúsculas y espacios ("Teléfono ").
// ============================================================================

/** Detecta el separador contando cuál aparece más en la primera línea. */
export function detectarSeparador(texto: string): string {
  const linea = texto.split(/\r?\n/)[0] ?? "";
  const cuenta = (c: string) => (linea.match(new RegExp(`\\${c}`, "g")) ?? []).length;
  const punto = cuenta(";");
  const coma = cuenta(",");
  const tab = cuenta("\t");
  if (tab > punto && tab > coma) return "\t";
  return punto > coma ? ";" : ",";
}

/** Parte una línea respetando comillas. */
function partirLinea(linea: string, sep: string): string[] {
  const campos: string[] = [];
  let actual = "";
  let entreComillas = false;
  for (let i = 0; i < linea.length; i++) {
    const c = linea[i];
    if (entreComillas) {
      if (c === '"') {
        if (linea[i + 1] === '"') { actual += '"'; i++; }   // comilla escapada
        else entreComillas = false;
      } else actual += c;
    } else if (c === '"') {
      entreComillas = true;
    } else if (c === sep) {
      campos.push(actual); actual = "";
    } else actual += c;
  }
  campos.push(actual);
  return campos.map((c) => c.trim());
}

/** Quita tildes, espacios y mayúsculas para comparar cabeceras. */
export function claveCabecera(s: string): string {
  return s.normalize("NFD").replace(/[̀-ͯ]/g, "")
    .toLowerCase().replace(/[^a-z0-9]/g, "");
}

/** Nombres que puede tener cada columna en un archivo real. */
const ALIAS: Record<string, string[]> = {
  nombre: ["nombre", "nombres", "cliente", "contacto", "nombrecompleto", "name", "fullname"],
  telefono: ["telefono", "celular", "movil", "whatsapp", "numero", "tel", "phone", "mobile"],
  etiquetas: ["etiquetas", "etiqueta", "tags", "tag"],
  notas: ["notas", "nota", "observaciones", "comentarios"],
};

export interface FilaCsv {
  /** 1-indexada y contando la cabecera, para que coincida con lo que ve en Excel. */
  linea: number;
  nombre: string;
  telefono: string;
  etiquetas: string[];
  notas: string;
}

export interface LecturaCsv {
  filas: FilaCsv[];
  /** Qué columna del archivo se usó para cada campo (para mostrárselo y que confirme). */
  mapeo: Partial<Record<keyof typeof ALIAS, string>>;
  error?: string;
}

/**
 * Lee el CSV completo. No valida teléfonos —de eso se encarga `normalizarTelefono`—:
 * aquí solo se resuelve la estructura.
 */
export function leerCsv(texto: string): LecturaCsv {
  // El BOM de Excel deja la primera cabecera como "﻿nombre" y no matchea nada.
  const limpio = texto.replace(/^﻿/, "");
  const lineas = limpio.split(/\r?\n/).filter((l) => l.trim() !== "");
  if (lineas.length === 0) return { filas: [], mapeo: {}, error: "El archivo está vacío." };

  const sep = detectarSeparador(limpio);
  const cabeceras = partirLinea(lineas[0], sep).map(claveCabecera);

  const indice: Partial<Record<keyof typeof ALIAS, number>> = {};
  const mapeo: LecturaCsv["mapeo"] = {};
  const cabecerasOriginales = partirLinea(lineas[0], sep);
  for (const [campo, alias] of Object.entries(ALIAS) as [keyof typeof ALIAS, string[]][]) {
    const i = cabeceras.findIndex((h) => alias.includes(h));
    if (i >= 0) { indice[campo] = i; mapeo[campo] = cabecerasOriginales[i]; }
  }

  if (indice.telefono === undefined) {
    return {
      filas: [], mapeo,
      error: `No encontré la columna del teléfono. La cabecera puede llamarse: ${ALIAS.telefono.join(", ")}.`,
    };
  }

  const filas: FilaCsv[] = [];
  for (let i = 1; i < lineas.length; i++) {
    const c = partirLinea(lineas[i], sep);
    const en = (campo: keyof typeof ALIAS) => {
      const j = indice[campo];
      return j === undefined ? "" : (c[j] ?? "").trim();
    };
    const etiquetas = en("etiquetas")
      .split(/[,|]/).map((t) => t.trim()).filter(Boolean);
    filas.push({
      linea: i + 1,
      nombre: en("nombre"),
      telefono: en("telefono"),
      etiquetas,
      notas: en("notas"),
    });
  }
  return { filas, mapeo };
}
